import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
    Select, MenuItem, FormControl, InputLabel, Chip
} from '@mui/material';
import { ResourceType, Subcontractor, SubcontractorRequest } from '../types/subcontractTypes';
import { SubcontractPermissions } from '../contexts/constants/subcontractPermissions';
import { formatRUT, validateRUT } from '../utils/validation';

/**
 * Opciones de alcance de recurso. Se usan los mismos colores que en el reporte diario:
 * azul para personal y ámbar para equipos.
 */
const RESOURCE_TYPE_OPTIONS: { value: ResourceType; label: string; color: string; hoverColor: string }[] = [
    { value: 'PERSONNEL', label: 'Personal', color: '#1976d2', hoverColor: '#1565c0' },
    { value: 'EQUIPMENT', label: 'Equipos', color: '#e0a800', hoverColor: '#c69500' }
];

interface SubcontractFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (request: SubcontractorRequest) => Promise<void>;
    /** Empresa a editar. Si es null, el formulario abre en modo creación. */
    subcontractor: Subcontractor | null;
    /** Especialidades disponibles, tomadas de activities.section. */
    availableSections: string[];
    /** Proyectos disponibles para asignar. */
    availableProjects: { id: number; name: string }[];
    permissions: SubcontractPermissions;
    saving?: boolean;
}

const SubcontractForm: React.FC<SubcontractFormProps> = ({
    open,
    onClose,
    onSubmit,
    subcontractor,
    availableSections,
    availableProjects,
    permissions,
    saving = false
}) => {
    const isEditMode = !!subcontractor;

    const [rut, setRut] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [operationalStatus, setOperationalStatus] = useState(true);
    const [selectedSections, setSelectedSections] = useState<string[]>([]);
    const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
    const [selectedResourceTypes, setSelectedResourceTypes] = useState<ResourceType[]>([]);
    const [errors, setErrors] = useState<{ rut?: boolean; businessName?: boolean; resourceTypes?: boolean }>({});
    // Mensaje bajo el campo, con el mismo comportamiento que el mantenedor de personal.
    const [errorRutMessage, setErrorRutMessage] = useState('');

    // Al abrir: en creación el formulario parte completamente limpio, sin arrastrar datos
    // anteriores; en edición carga el estado exacto de la fila seleccionada.
    useEffect(() => {
        if (!open) return;

        if (subcontractor) {
            // El backend guarda el RUT normalizado; se muestra con el mismo formato que personal.
            setRut(formatRUT(subcontractor.rut).toUpperCase());
            setBusinessName(subcontractor.businessName);
            setOperationalStatus(subcontractor.operationalStatus);
            setSelectedSections([...subcontractor.sections]);
            setSelectedProjectIds(subcontractor.projects.map(p => p.id));
            setSelectedResourceTypes([...(subcontractor.resourceTypes || [])]);
        } else {
            setRut('');
            setBusinessName('');
            setOperationalStatus(true);
            setSelectedSections([]);
            setSelectedProjectIds([]);
            // Una empresa nueva parte aportando ambos tipos, que es el caso habitual.
            setSelectedResourceTypes(['PERSONNEL', 'EQUIPMENT']);
        }
        setErrors({});
        setErrorRutMessage('');
    }, [open, subcontractor]);

    /**
     * Mismo comportamiento que el campo RUT del mantenedor de personal
     * (EmployeeForm.handleInputChange): formatea mientras se escribe con formatRUT,
     * pasa a mayusculas y valida el digito verificador en cada tecla.
     */
    const handleRutChange = (value: string) => {
        const formattedRut = formatRUT(value).toUpperCase();
        setRut(formattedRut);

        if (!validateRUT(formattedRut)) {
            setErrorRutMessage('RUT no válido');
            setErrors(prev => ({ ...prev, rut: true }));
        } else {
            setErrorRutMessage('');
            setErrors(prev => ({ ...prev, rut: false }));
        }
    };

    const toggleSection = (section: string) => {
        setSelectedSections(prev =>
            prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
        );
    };

    const toggleProject = (projectId: number) => {
        setSelectedProjectIds(prev =>
            prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]
        );
    };

    const toggleResourceType = (resourceType: ResourceType) => {
        setSelectedResourceTypes(prev =>
            prev.includes(resourceType) ? prev.filter(item => item !== resourceType) : [...prev, resourceType]
        );
    };

    const handleSubmit = async () => {
        // Misma condicion de bloqueo que en el mantenedor de personal: el RUT debe estar
        // presente y ser valido segun modulo 11.
        const isRutInvalid = !rut.trim() || !validateRUT(rut);
        const isBusinessNameInvalid = !businessName.trim();
        // Sin alcance de recurso la empresa no aparecería en ningún área del reporte diario.
        const isResourceTypeInvalid = selectedResourceTypes.length === 0;

        setErrors({
            rut: isRutInvalid,
            businessName: isBusinessNameInvalid,
            resourceTypes: isResourceTypeInvalid
        });

        if (isRutInvalid && rut.trim()) {
            setErrorRutMessage('RUT no válido');
        }

        if (isRutInvalid || isBusinessNameInvalid || isResourceTypeInvalid) {
            return;
        }

        // El backend vuelve a validar y normaliza el RUT antes de persistir.
        await onSubmit({
            id: subcontractor?.id,
            rut: rut.trim(),
            businessName: businessName.trim(),
            operationalStatus,
            sections: selectedSections,
            projectIds: selectedProjectIds,
            resourceTypes: selectedResourceTypes
        });
    };

    const canEditBasic = isEditMode ? permissions.canEditBasicData : permissions.canCreate;
    const canEditSections = isEditMode ? permissions.canEditSections : permissions.canCreate;
    const canEditProjects = isEditMode ? permissions.canEditProjects : permissions.canCreate;
    const canEditStatus = isEditMode ? permissions.canChangeOperationalStatus : permissions.canCreate;
    // El alcance de recurso se rige por el mismo permiso que la especialidad, por ser también
    // una definición de qué puede hacer la empresa y no un dato de identificación.
    const canEditResourceTypes = isEditMode ? permissions.canEditSections : permissions.canCreate;

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', color: '#004F86' }}>
                {isEditMode ? 'Editar Empresa Subcontratista' : 'Agregar Empresa Subcontratista'}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                    <Box display="flex" flexDirection="column" sx={{ width: '40%' }}>
                        <TextField
                            label="RUT Empresa"
                            name="rut"
                            placeholder="Ej: 12345678-9"
                            value={rut}
                            onChange={(e) => handleRutChange(e.target.value)}
                            variant="outlined"
                            fullWidth
                            error={Boolean(errors.rut)}
                            helperText={errors.rut ? 'RUT inválido' : ''}
                            required
                            inputProps={{ maxLength: 12 }}
                            disabled={!canEditBasic}
                        />
                        <Typography variant="caption" color="error" sx={{ minHeight: '20px' }}>
                            {errorRutMessage}
                        </Typography>
                    </Box>
                    <TextField
                        label="Razón Social"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        variant="outlined"
                        error={Boolean(errors.businessName)}
                        helperText={errors.businessName ? 'La razón social es obligatoria' : ''}
                        required
                        disabled={!canEditBasic}
                        fullWidth
                    />
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#004F86', mb: 1 }}>
                    Especialidades / Servicios
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    {availableSections.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                            No hay secciones cargadas en el mantenedor de Actividades.
                        </Typography>
                    )}
                    {availableSections.map(section => {
                        const selected = selectedSections.includes(section);
                        return (
                            <Chip
                                key={section}
                                label={section}
                                onClick={canEditSections ? () => toggleSection(section) : undefined}
                                variant={selected ? 'filled' : 'outlined'}
                                sx={{
                                    cursor: canEditSections ? 'pointer' : 'default',
                                    backgroundColor: selected ? '#1976d2' : 'white',
                                    color: selected ? 'white' : '#1976d2',
                                    borderColor: '#1976d2',
                                    fontWeight: selected ? 'bold' : 'normal',
                                    '&:hover': { backgroundColor: selected ? '#1565c0' : '#e3f2fd' }
                                }}
                            />
                        );
                    })}
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#004F86', mb: 1 }}>
                    Proyectos Asignados
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    {availableProjects.map(project => {
                        const selected = selectedProjectIds.includes(project.id);
                        return (
                            <Chip
                                key={project.id}
                                label={project.name}
                                onClick={canEditProjects ? () => toggleProject(project.id) : undefined}
                                variant={selected ? 'filled' : 'outlined'}
                                sx={{
                                    cursor: canEditProjects ? 'pointer' : 'default',
                                    backgroundColor: selected ? '#2e7d32' : 'white',
                                    color: selected ? 'white' : '#2e7d32',
                                    borderColor: '#2e7d32',
                                    fontWeight: selected ? 'bold' : 'normal',
                                    '&:hover': { backgroundColor: selected ? '#1b5e20' : '#e8f5e9' }
                                }}
                            />
                        );
                    })}
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#004F86', mb: 1 }}>
                    Tipo de Recurso que Aporta
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {RESOURCE_TYPE_OPTIONS.map(option => {
                        const selected = selectedResourceTypes.includes(option.value);
                        return (
                            <Chip
                                key={option.value}
                                label={option.label}
                                onClick={canEditResourceTypes ? () => toggleResourceType(option.value) : undefined}
                                variant={selected ? 'filled' : 'outlined'}
                                sx={{
                                    cursor: canEditResourceTypes ? 'pointer' : 'default',
                                    backgroundColor: selected ? option.color : 'white',
                                    color: selected ? 'white' : option.color,
                                    borderColor: option.color,
                                    fontWeight: selected ? 'bold' : 'normal',
                                    '&:hover': { backgroundColor: selected ? option.hoverColor : '#f5f5f5' }
                                }}
                            />
                        );
                    })}
                </Box>
                <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 3, color: errors.resourceTypes ? 'error.main' : 'text.secondary' }}
                >
                    {errors.resourceTypes
                        ? 'Seleccione al menos un tipo de recurso'
                        : 'Define en qué áreas del reporte diario aparecerá la empresa'}
                </Typography>

                <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel id="operational-status-label">Estado de Disponibilidad</InputLabel>
                    <Select
                        labelId="operational-status-label"
                        label="Estado de Disponibilidad"
                        value={operationalStatus ? 'active' : 'inactive'}
                        onChange={(e) => setOperationalStatus(e.target.value === 'active')}
                        disabled={!canEditStatus}
                    >
                        <MenuItem value="active">Activo (disponible para el Reporte Diario)</MenuItem>
                        <MenuItem value="inactive">Inactivo (bloqueada para nuevos reportes)</MenuItem>
                    </Select>
                </FormControl>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={saving}>
                    Cancelar
                </Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving}>
                    {saving ? 'Guardando…' : (isEditMode ? 'Actualizar Cambios' : 'Guardar Empresa')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SubcontractForm;
